import { useCallback, useEffect, useState } from 'react'
import {
  getDemoReadiness,
  listDemoUnlockedWorkspaces,
  type DemoReadiness,
} from '../../../services/ownerDemoLaneService'
import type { OwnerWorkspaceListItem } from '../../../services/ownerWorkspacesService'
import { useSupportAccessReason } from '../shell/SupportAccessReasonDialog'

const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error'

export type DemoWorkspacesState = {
  workspaces: OwnerWorkspaceListItem[] | null
  loading: boolean
  error: string | null
}

/** Loads the demo-unlocked workspaces once (when enabled). */
export function useDemoWorkspaces(enabled: boolean): DemoWorkspacesState {
  const [result, setResult] = useState<{ workspaces: OwnerWorkspaceListItem[] | null; error: string | null } | null>(null)

  useEffect(() => {
    if (!enabled) return undefined
    let cancelled = false
    listDemoUnlockedWorkspaces().then(
      (workspaces) => { if (!cancelled) setResult({ workspaces, error: null }) },
      (error: unknown) => { if (!cancelled) setResult({ workspaces: null, error: messageOf(error) }) },
    )
    return () => { cancelled = true }
  }, [enabled])

  return { workspaces: result?.workspaces ?? null, loading: enabled && result === null, error: result?.error ?? null }
}

export type DemoReadinessState = {
  data: DemoReadiness | null
  loading: boolean
  error: string | null
  reload: () => void
}

/**
 * Runs the readiness checks for one workspace. Re-runs when the workspace, `refreshToken` (bumped by the
 * freeze control after a change) or `reload()` changes. Loading is derived from the request key, so no state is
 * set in the effect body.
 */
export function useDemoReadiness(workspaceId: string | null, refreshToken = 0): DemoReadinessState {
  const { ensureAccessReason } = useSupportAccessReason()
  const [nonce, setNonce] = useState(0)
  const [result, setResult] = useState<{ key: string; workspaceId: string; data: DemoReadiness | null; error: string | null } | null>(null)
  const key = workspaceId ? `${workspaceId}|${nonce}|${refreshToken}` : ''

  useEffect(() => {
    if (!workspaceId) return undefined
    let cancelled = false
    void (async () => {
      const reason = await ensureAccessReason({
        key: `DEMO_WORKSPACE:${workspaceId}`,
        defaultCode: 'ROUTINE_REVIEW',
        defaultTicketId: workspaceId,
      })
      if (!reason || cancelled) return
      getDemoReadiness(workspaceId, reason).then(
        (data) => { if (!cancelled) setResult({ key, workspaceId, data, error: null }) },
        (error: unknown) => { if (!cancelled) setResult({ key, workspaceId, data: null, error: messageOf(error) }) },
      )
    })()
    return () => { cancelled = true }
  }, [workspaceId, key, ensureAccessReason])

  const reload = useCallback(() => setNonce((value) => value + 1), [])
  const current = result && result.workspaceId === workspaceId ? result : null
  return {
    data: current?.data ?? null,
    loading: workspaceId !== null && result?.key !== key,
    error: current?.error ?? null,
    reload,
  }
}
